<script setup lang="ts">
// 일기장: 텍스트 + 음성(암호화 보관만, 분석 안 함) + 캡처(기기 OCR → 답장 연결). decisions 참고.
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVaultStore, type Entry, type Att } from '../../stores/vault'
import { useUiStore } from '../../stores/ui'
import { ocrImage } from '../../lib/ocr'
import { icon } from '../../lib/icons'

const { t, locale } = useI18n()
const vault = useVaultStore()
const ui = useUiStore()
const emit = defineEmits<{ toast: [msg: string]; grow: [foundIds: string[]]; useThread: [text: string]; newPeople: [names: string[]] }>()

const text = ref('')
const filter = ref<string>('all')
const personById = (id: string) => vault.people.find(p => p.id === id)

// ── 작성 중 첨부(로컬) ──
interface Draft { uid: string; kind: 'voice' | 'image'; mime: string; b64: string; url: string; sec?: number; ocr?: string; ocrBusy?: boolean }
const draftAtt = ref<Draft[]>([])
const urls = reactive<Record<string, string>>({}) // 타임라인 첨부 data URL 캐시

const draftTags = computed(() => vault.people.filter(p => p.name && text.value.includes(p.name)))

function blobToB64(blob: Blob): Promise<{ b64: string; url: string }> {
  return new Promise(res => { const fr = new FileReader(); fr.onload = () => { const url = String(fr.result); res({ b64: url.split(',')[1] || '', url }) }; fr.readAsDataURL(blob) })
}

// ── 녹음(MediaRecorder). 분석하지 않고 암호화 보관만 ──
const recording = ref(false)
const recSec = ref(0)
let recorder: MediaRecorder | null = null
let chunks: Blob[] = []
let recTimer: number | undefined
async function toggleRec() {
  if (recording.value) { recorder?.stop(); return }
  try {
    ui.holdAway() // 마이크 권한 창이 떠도 잠금되지 않게
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    chunks = []; recSec.value = 0
    recorder = new MediaRecorder(stream)
    recorder.ondataavailable = e => e.data.size && chunks.push(e.data)
    recorder.onstop = async () => {
      stream.getTracks().forEach(tk => tk.stop())
      clearInterval(recTimer); recording.value = false
      const blob = new Blob(chunks, { type: recorder!.mimeType || 'audio/webm' })
      const { b64, url } = await blobToB64(blob)
      draftAtt.value.push({ uid: crypto.randomUUID(), kind: 'voice', mime: blob.type, b64, url, sec: Math.max(recSec.value, 1) })
    }
    recorder.start()
    recording.value = true
    recTimer = window.setInterval(() => recSec.value++, 1000)
  } catch { emit('toast', t('diary.noMic')) }
}

// ── 캡처(이미지 파일) ──
const fileInput = ref<HTMLInputElement | null>(null)
async function onFile(e: Event) {
  const f = (e.target as HTMLInputElement).files?.[0]
  if (!f) return
  const { b64, url } = await blobToB64(f)
  draftAtt.value.push({ uid: crypto.randomUUID(), kind: 'image', mime: f.type || 'image/png', b64, url })
  ;(e.target as HTMLInputElement).value = ''
}
async function runOcr(a: Draft) {
  a.ocrBusy = true
  try { a.ocr = (await ocrImage(a.url)) || t('diary.ocrFail') } catch { a.ocr = t('diary.ocrFail') }
  a.ocrBusy = false
}
function pickFile() { ui.holdAway(); fileInput.value?.click() } // 파일 선택 창이 떠도 잠금되지 않게
function removeDraft(uid: string) { draftAtt.value = draftAtt.value.filter(a => a.uid !== uid) }
const fmt = (s = 0) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

// ── 저장 ──
async function save() {
  const txt = text.value.trim()
  if (!txt && !draftAtt.value.length) return
  const metas: Att[] = []
  for (const a of draftAtt.value) {
    const id = await vault.addAttachment(a.kind, a.mime, a.b64)
    metas.push({ id, kind: a.kind, sec: a.sec, ocr: a.ocr })
    urls[id] = a.url // 바로 보이도록 캐시 선반영
  }
  const { insight, foundIds, newNames } = await vault.logEntry(txt, metas.length ? metas : undefined)
  text.value = ''; draftAtt.value = []; filter.value = 'all'
  emit('toast', insight); emit('grow', foundIds)
  if (newNames.length) emit('newPeople', newNames)
}

// ── 타임라인 ──
function dayLabel(iso?: string) {
  if (!iso) return t('diary.today')
  const d = new Date(iso); if (isNaN(d.getTime())) return t('diary.today')
  const now = new Date()
  const diff = Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / 86400000)
  if (diff === 0) return t('diary.today')
  if (diff === 1) return t('diary.yesterday')
  return locale.value === 'en' ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : `${d.getMonth() + 1}월 ${d.getDate()}일`
}
function timeLabel(iso?: string) { if (!iso) return ''; const d = new Date(iso); return isNaN(d.getTime()) ? '' : `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}` }

const usedPeople = computed(() => { const ids = new Set(vault.entries.flatMap(e => e.people || [])); return vault.people.filter(p => ids.has(p.id)) })
const groups = computed(() => {
  const list = [...vault.entries].reverse().filter(e => filter.value === 'all' || (e.people || []).includes(filter.value))
  const out: { day: string; items: Entry[] }[] = []
  for (const e of list) { const d = dayLabel(e.date); if (!out.length || out[out.length - 1].day !== d) out.push({ day: d, items: [] }); out[out.length - 1].items.push(e) }
  return out
})
// 타임라인 첨부 URL 로드(지연 복호화)
watch(() => vault.entries, async (es) => {
  for (const e of es) for (const a of (e.att || [])) if (!urls[a.id]) { const u = await vault.getAttachmentUrl(a.id); if (u) urls[a.id] = u }
}, { immediate: true, deep: true })
</script>

<template>
  <div class="dy">
    <div class="dy-safe"><span v-html="icon('lock')"></span>{{ t('diary.safe') }}</div>

    <div class="dy-new">
      <textarea v-model="text" :placeholder="t('diary.placeholder')" @keydown.enter.meta.prevent="save" @keydown.enter.ctrl.prevent="save"></textarea>
      <div v-if="draftTags.length" class="dy-tags">
        <span v-for="p in draftTags" :key="p.id" class="dy-tag"><i class="dy-av" :style="{ '--c1': p.c1, '--c2': p.c2 }"></i>{{ p.name }}</span>
      </div>

      <!-- 작성 중 첨부 미리보기 -->
      <div v-if="recording" class="dy-recording"><span class="dot"></span><span class="t">{{ fmt(recSec) }}</span><span style="font-size:13px;color:#E5484D">{{ t('diary.recNote') }}</span></div>
      <div v-if="draftAtt.length" class="dy-att">
        <div v-for="a in draftAtt" :key="a.uid">
          <div v-if="a.kind === 'voice'" class="dy-voice">
            <audio :src="a.url" controls preload="metadata"></audio>
            <small>{{ fmt(a.sec) }}</small>
            <button class="dy-x" @click="removeDraft(a.uid)" aria-label="삭제">×</button>
          </div>
          <div v-else>
            <div class="dy-cap"><img :src="a.url" alt="캡처 이미지"><button class="dy-x" @click="removeDraft(a.uid)" aria-label="삭제">×</button></div>
            <button v-if="!a.ocr && !a.ocrBusy" class="dy-tool" @click="runOcr(a)">{{ t('diary.ocrRun') }}</button>
            <div v-else-if="a.ocrBusy" class="dy-read"><small>{{ t('diary.ocrBusy') }}</small></div>
            <div v-else class="dy-read"><small>{{ t('diary.ocrTitle') }}</small>{{ a.ocr }}
              <button @click="emit('useThread', a.ocr || '')">{{ t('diary.useReply') }}</button></div>
          </div>
        </div>
      </div>

      <div class="dy-bar">
        <button class="dy-tool" :class="{ rec: recording }" @click="toggleRec"><span v-html="icon(recording ? 'stop' : 'mic')"></span>{{ recording ? t('diary.recStop') : t('diary.rec') }}</button>
        <button class="dy-tool" @click="pickFile"><span v-html="icon('image')"></span>{{ t('diary.cap') }}</button>
        <input ref="fileInput" type="file" accept="image/*" hidden @change="onFile" />
        <button class="dy-save" :disabled="!text.trim() && !draftAtt.length" @click="save">{{ t('diary.save') }}</button>
      </div>
    </div>

    <div class="dy-filter">
      <button class="all" :class="{ sel: filter === 'all' }" @click="filter = 'all'">{{ t('diary.all') }} {{ vault.entries.length }}</button>
      <button v-for="p in usedPeople" :key="p.id" :class="{ sel: filter === p.id }" @click="filter = p.id"><i class="dy-av" :style="{ '--c1': p.c1, '--c2': p.c2 }"></i>{{ p.name }}</button>
    </div>

    <div v-if="!groups.length" class="dy-empty">{{ t('diary.empty') }}</div>
    <template v-for="g in groups" :key="g.day">
      <div class="dy-day">{{ g.day }}</div>
      <div v-for="e in g.items" :key="e.id" class="dy-e">
        <div class="dy-eh">
          <span v-for="id in (e.people || [])" :key="id" class="dy-tag"><i v-if="personById(id)" class="dy-av" :style="{ '--c1': personById(id)!.c1, '--c2': personById(id)!.c2 }"></i>{{ personById(id)?.name || '?' }}</span>
          <time>{{ timeLabel(e.date) }}</time>
        </div>
        <div v-if="e.text" class="dy-txt">{{ e.text }}</div>
        <div v-if="e.att && e.att.length" class="dy-att">
          <div v-for="a in e.att" :key="a.id">
            <div v-if="a.kind === 'voice'" class="dy-voice"><audio v-if="urls[a.id]" :src="urls[a.id]" controls preload="metadata"></audio><small>{{ fmt(a.sec) }}</small></div>
            <div v-else>
              <div class="dy-cap"><img v-if="urls[a.id]" :src="urls[a.id]" alt="캡처 이미지"></div>
              <div v-if="a.ocr" class="dy-read"><small>{{ t('diary.ocrTitle') }}</small>{{ a.ocr }}<button @click="emit('useThread', a.ocr || '')">{{ t('diary.useReply') }}</button></div>
            </div>
          </div>
        </div>
        <div v-if="e.insight" class="dy-ins">{{ e.insight }}</div>
      </div>
    </template>
  </div>
</template>
